import { defineStore, type StoreDefinition } from "pinia";
import AuthorizationService from "@/Pages/Login/services/auth.service";
import { type EmsAgency, FeatureFlag, type Hospital, type TwiageCase } from "@/App/types/twiageTypes";
import UserService from "@/Pages/Login/services/user.service";
import router from "@/App/router/router";
import { EventBus, EventType } from "@/App/services/eventBus";
import type { User, UserPreferences } from "@/App/user/User";
import { useCasesStore, useOptionsStore, useStatusesStore, useUnseenCasesStore } from "@/App/stores";
import { useWhatsNewStore } from "@/App/whatsNew/whatsNew.store";
import { useHospitalsStatusesStore } from "@/Pages/Statuses/store";
import type { UserStoreActions } from "@/App/user/UserStoreActions";
import type { UserStoreGetters } from "@/App/user/UserStoreGetters";
import { windowsService } from "@/App/services/windowsService";
import config from "@/App/config/main";
import { hasSupportedFeature } from "@/Pages/Cases/functions/hasSupportedFeature";
import { RouteName } from "@/App/router/routeName";
import type { DefaultMapView } from "../components/Map/Map";
import { userHasFeatureEnabled, userHasFeatureForOrganization } from "@/App/user/userHasFeatureEnabled";
import { FilterType } from "@/Pages/Cases/filtering/FilterType";
import type { FilterUI } from "@/App/user/FilterUI";
import type { FilterItem } from "@/Pages/Cases/filtering/components/FilterItem";
import { nameSort } from "@/Pages/Cases/functions";
import { UserRole } from "@/App/user/UserRole";
import { captureAppOpen, clearSentryUser, setSentryUser } from "@/App/services/MonitoringService";

export type UserStore = StoreDefinition<string, UserStoreState, UserStoreGetters, UserStoreActions>;

export interface UserStoreState {
  user: User | null;
}

EventBus.on({
  eventType: EventType.onLogout,
  callback: (payload) => useUserStore().logout({ reason: payload as string }),
});

export const useUserStore: UserStore = defineStore({
  id: "user",
  state: (): UserStoreState => ({
    user: UserService.getUser(),
  }),
  actions: {
    async logout({ reason }: { reason?: string } = { reason: undefined }): Promise<void> {
      this.user = null;
      clearSentryUser();
      useCasesStore().onSessionEnd();
      useUnseenCasesStore().onSessionEnd();
      useHospitalsStatusesStore().onSessionEnd();
      useStatusesStore().onSessionEnd();
      // TODO put in try catch, will fail with no internet connection presented
      AuthorizationService.logout({ reason });
      if (AuthorizationService.isUserPreferToLoginViaSso()) {
        windowsService.goTo({ url: AuthorizationService.getSsoSignoutUrl() ?? config.SSO_URL });
      } else {
        await router.replace({ name: RouteName.login });
      }
    },
    onAppStarted() {
      if (this.user) {
        setSentryUser(this.user);
        captureAppOpen();
        UserService.retrieveUserData().catch(console.log);
      }
    },
    onUserLoggedIn(user: User): void {
      this.user = user;
      setSentryUser(user);
      captureAppOpen();
      useOptionsStore().loadState();
      useWhatsNewStore().checkWhatsNew(this.user?.whatsNew?.lastSeen);
    },
    setUserPreferences(preferences: UserPreferences): void {
      this.user = UserService.updateUserPreferences(preferences);
    },
  },
  getters: {
    hasMultipleHospitals(state: UserStoreState): boolean {
      return (state.user?.hospitalsToFilterWith?.length ?? 0) > 1;
    },
    getHospitals(state: UserStoreState): Hospital[] {
      return state.user?.hospitalsToFilterWith ?? [];
    },
    getCasesFilters(state: UserStoreState): FilterUI[] {
      const getFilterItems = (filters: { name: string; id: string }[] | undefined): FilterItem[] => {
        return filters?.sort?.(nameSort).map((item) => ({ name: item.name, selected: false, id: item.id })) || [];
      };
      return [
        {
          name: "Hospitals",
          type: FilterType.hospitals,
          value: getFilterItems(this.getHospitals),
        },
        {
          name: "EMS Agencies",
          type: FilterType.ems,
          value: getFilterItems(state.user?.emsAgenciesToFilterWith ?? []),
        },
        {
          name: "Complaints",
          type: FilterType.complaints,
          value: getFilterItems(state.user?.complaintsToFilterWith ?? []),
        },
      ];
    },
    getMihDefaultMapView: (state: UserStoreState): DefaultMapView | undefined =>
      state.user?.mih?.emsAgencies.find((emsAgency) => emsAgency.mih?.defaultMapView)?.mih?.defaultMapView,
    googleMapsKey: (): string => UserService.getGoogleMapsApiKey(),
    isBigBoard: (state: UserStoreState): boolean => !!state.user?.roles?.includes(UserRole.bigboard),
    getUserWhatsNewKey: (state: UserStoreState): string => `whats_new_${state.user?.id ?? "none"}`,
    canBroadcast(state: UserStoreState): boolean {
      return (state.user?.broadcastHospitals?.length ?? 0) > 0;
    },
    canManageHospitalStatuses(): boolean {
      return this.isHospitalAdmin || this.isFeatureEnabled(FeatureFlag.manageStatuses);
    },
    isMedcomUser:
      (state: UserStoreState) =>
      (twiageCase: TwiageCase | undefined): boolean => {
        if (twiageCase) {
          return (
            state.user?.emsAgenciesMedcom?.some((agency) => agency.id === twiageCase.emsAgency?.id) == true &&
            !state.user?.hospitalMemberships?.some((hospital) => hospital.id === twiageCase.destinationHospital?.id)
          );
        } else return (state.user?.emsAgenciesMedcom?.length ?? 0) > 0;
      },
    isFeatureEnabled:
      (state: UserStoreState) =>
      (feature: FeatureFlag): boolean => {
        return userHasFeatureEnabled({ user: state.user, feature });
      },
    canEditIftCase(state: UserStoreState): (twiageCase: TwiageCase) => boolean {
      return (twiageCase: TwiageCase) => {
        return (
          userHasFeatureForOrganization({
            user: state.user,
            feature: FeatureFlag.Ift,
            organizationId: twiageCase.ift?.source.hospital?.id,
          }) ||
          userHasFeatureForOrganization({
            user: state.user,
            feature: FeatureFlag.canIftDispatch,
            organizationId: twiageCase.ift?.assigned?.emsAgency.id,
          })
        );
      };
    },
    canUserCreateIftOrMihCases(): boolean {
      return this.isFeatureEnabled(FeatureFlag.IftEmsCreateCases) || this.mihEnabled;
    },
    isFeatureEnabledForHospitalId:
      (state: UserStoreState) =>
      (feature: FeatureFlag, hospitalId?: string): boolean => {
        return (
          (!!hospitalId &&
            state.user?.hospitalMemberships?.some(
              ({ id, supportedFeatures }) =>
                id === hospitalId && supportedFeatures?.some((f) => f.name === feature && f.enabled),
            )) ??
          false
        );
      },
    iftEnabled(): boolean {
      return this.isFeatureEnabled(FeatureFlag.Ift) || this.isFeatureEnabled(FeatureFlag.IftDispatch);
    },
    mihEnabled(): boolean {
      return this.isFeatureEnabled(FeatureFlag.Mih);
    },
    iftInviteEmsEnabled(): boolean {
      return this.isFeatureEnabled(FeatureFlag.IftDispatch);
    },
    canSeeEdCases(): boolean {
      return this.isFeatureEnabled(FeatureFlag.canSeeEdCases);
    },
    isHospitalAdmin(state: UserStoreState): boolean {
      return (state.user?.hospitalsAdministered?.length ?? 0) > 0;
    },
    isEmsAdmin(state: UserStoreState): boolean {
      return (state.user?.emsAgenciesAdministered?.length ?? 0) > 0;
    },
    isIftReportsAvailable(): boolean {
      return this.iftEnabled && (this.isHospitalAdmin || this.isEmsAdmin);
    },
    emsMemberships(): EmsAgency[] {
      return this.user?.emsAgencyMemberships ?? [];
    },
    isAlwaysOnLocationTurnedOn(): boolean {
      return this.emsMemberships.some((ems) =>
        hasSupportedFeature({ organization: ems, feature: FeatureFlag.locationAlwaysOn }),
      );
    },
    iftReportColumns(): string[] | undefined {
      const reportColumns = this.user?.ift?.emsAgencies[0]?.reportColumns;
      if (!reportColumns || reportColumns.length === 0) {
        return undefined;
      }
      return reportColumns.map((col) => col.key ?? "");
    },
  },
});
